import { createBrowserRouter } from 'react-router';
import { AppLayout } from '#client/shell/AppLayout';
import { catalogueRoutes, gate } from '#client/shell/route-gates';
import { AnalyticsLayout } from '#client/features/analytics/AnalyticsLayout';
import { FlowPage } from '#client/features/analytics/FlowPage';
import { OverviewPage } from '#client/features/analytics/OverviewPage';
import { PatientMixPage } from '#client/features/analytics/PatientMixPage';
import { QualityPage } from '#client/features/analytics/QualityPage';
import { StaffPage } from '#client/features/analytics/StaffPage';
import { SupplyPage } from '#client/features/analytics/SupplyPage';
import { VolumePage } from '#client/features/analytics/VolumePage';
import { DashboardPage } from '#client/features/dashboard/DashboardPage';
import { LoginPage } from '#client/features/auth/LoginPage';
import { NotFoundPage } from '#client/features/NotFoundPage';
import { WorklistPage } from '#client/features/worklist/WorklistPage';
import { ReportEntryPage } from '#client/features/reports/ReportEntryPage';
import { PrintPage } from '#client/features/print/PrintPage';
import { TemplateBuilderPage } from '#client/features/print/templates/TemplateBuilderPage';
import { TemplateListPage } from '#client/features/print/templates/TemplateListPage';
import { PatientDetailPage } from '#client/features/patients/PatientDetailPage';
import { PatientEditPage } from '#client/features/patients/PatientEditPage';
import { PatientListPage } from '#client/features/patients/PatientListPage';
import { PatientRegistrationPage } from '#client/features/patients/PatientRegistrationPage';
import { LabTestDetailPage } from '#client/features/lab-tests/LabTestDetailPage';
import { LabTestFormPage } from '#client/features/lab-tests/LabTestFormPage';
import { LabTestListPage } from '#client/features/lab-tests/LabTestListPage';
import { PanelDetailPage } from '#client/features/panels/PanelDetailPage';
import { PanelFormPage } from '#client/features/panels/PanelFormPage';
import { PanelListPage } from '#client/features/panels/PanelListPage';
import {
  AntibioticDetailPage,
  AntibioticFormPage,
  AntibioticListPage,
  OrganismDetailPage,
  OrganismFormPage,
  OrganismListPage,
} from '#client/features/microbiology/NameCodePages';
import {
  ReportCommentDetailPage,
  ReportCommentFormPage,
  ReportCommentListPage,
  ReportRemarkDetailPage,
  ReportRemarkFormPage,
  ReportRemarkListPage,
} from '#client/features/report-phrases/PhrasePages';
import { TestParameterDetailPage } from '#client/features/test-parameters/TestParameterDetailPage';
import { TestParameterFormPage } from '#client/features/test-parameters/TestParameterFormPage';
import { TestParameterListPage } from '#client/features/test-parameters/TestParameterListPage';
import { ConsultantDetailPage } from '#client/features/consultants/ConsultantDetailPage';
import { ConsultantFormPage } from '#client/features/consultants/ConsultantFormPage';
import { ConsultantListPage } from '#client/features/consultants/ConsultantListPage';
import { DepartmentDetailPage } from '#client/features/departments/DepartmentDetailPage';
import { DepartmentFormPage } from '#client/features/departments/DepartmentFormPage';
import { DepartmentListPage } from '#client/features/departments/DepartmentListPage';
import { TestProcedureDetailPage } from '#client/features/procedures/TestProcedureDetailPage';
import { TestProcedureFormPage } from '#client/features/procedures/TestProcedureFormPage';
import { TestProcedureListPage } from '#client/features/procedures/TestProcedureListPage';
import { PatientTrendsPage } from '#client/features/reports/PatientTrendsPage';
import { ImportListPage } from '#client/features/imports/ImportListPage';
import { OrderRequestDetailPage } from '#client/features/order-requests/OrderRequestDetailPage';
import { OrderRequestFormPage } from '#client/features/order-requests/OrderRequestFormPage';
import { OrderRequestListPage } from '#client/features/order-requests/OrderRequestListPage';
import { RegisterRequestPage } from '#client/features/order-requests/RegisterRequestPage';
import { ReceiveStockPage } from '#client/features/procurement/ReceiveStockPage';
import { RequisitionDetailPage } from '#client/features/procurement/RequisitionDetailPage';
import { RequisitionFormPage } from '#client/features/procurement/RequisitionFormPage';
import { RequisitionListPage } from '#client/features/procurement/RequisitionListPage';
import { ImportWizardPage } from '#client/features/imports/ImportWizardPage';
import { InventoryPage } from '#client/features/inventory/InventoryPage';
import { ReagentItemDetailPage } from '#client/features/inventory/ReagentItemDetailPage';
import { ReagentItemFormPage } from '#client/features/inventory/ReagentItemFormPage';
import { RecipeFormPage } from '#client/features/inventory/RecipeFormPage';
import { RecipeListPage } from '#client/features/inventory/RecipeListPage';
import { StockEntryPage } from '#client/features/inventory/StockEntryPage';
import { StockLedgerPage } from '#client/features/inventory/StockLedgerPage';
import { ShareAuditPage } from '#client/features/whatsapp/ShareAuditPage';
import { SupplierFormPage } from '#client/features/inventory/SupplierFormPage';
import { SupplierListPage } from '#client/features/inventory/SupplierListPage';
import { RateListPage } from '#client/features/rate-list/RateListPage';
import { CalculatorPage } from '#client/features/finance/CalculatorPage';
import { DayClosePage } from '#client/features/finance/DayClosePage';
import { ExpenseFormPage } from '#client/features/finance/ExpenseFormPage';
import { ExpenseListPage } from '#client/features/finance/ExpenseListPage';
import { IncomePage } from '#client/features/finance/IncomePage';
import { ProfitLossPage } from '#client/features/finance/ProfitLossPage';
import { YearHistoryPage } from '#client/features/finance/YearHistoryPage';
import { BackupPage } from '#client/features/settings/BackupPage';
import { DatabaseHealthPage } from '#client/features/settings/DatabaseHealthPage';
import { CompanyDetailPage } from '#client/features/settings/CompanyDetailPage';
import { UserDetailPage } from '#client/features/users/UserDetailPage';
import { UserFormPage } from '#client/features/users/UserFormPage';
import { RoleFormPage } from '#client/features/roles/RoleFormPage';
import { RoleListPage } from '#client/features/roles/RoleListPage';
import { UserListPage } from '#client/features/users/UserListPage';

/**
 * Library-mode routing: no loaders or actions. Data fetching lives in the
 * components via the typed API client, which keeps this a plain SPA with no
 * SSR/loader machinery.
 *
 * ## Registration is not the index
 *
 * The registration form lives at `/patients/new`, not `/`, for three reasons:
 *
 *   * it is not a universal task — a technician cannot submit it, so the
 *     app's front door was a screen a third of the staff are forbidden to use;
 *   * `/` is where the logo, the browser home button and a stale tab all land,
 *     so a half-filled form was one accidental click from being discarded;
 *   * every other resource in this app already creates at `/x/new`.
 *
 * Registration stays one click away from everywhere via the sidebar's first
 * entry and the primary action on the patient list.
 *
 * ## Permission gating
 *
 * Every screen is gated on the permission its main request needs, through
 * `gate(...)` — one permission, or a list of which any one will do. It is
 * presentation only: every one of these paths is also enforced server-side,
 * and the roles an admin builds decide who passes.
 */
export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    element: <AppLayout />,
    children: [
      gate('dashboard.view', [{ index: true, element: <DashboardPage /> }]),
      gate('worklist.view', [{ path: 'worklist', element: <WorklistPage /> }]),

      gate('patient.view', [
        { path: 'patients', element: <PatientListPage /> },
        { path: 'patients/:id', element: <PatientDetailPage /> },
        { path: 'patients/:id/print', element: <PrintPage /> },
        { path: 'patients/:id/trends', element: <PatientTrendsPage /> },
      ]),
      // Static segments outrank dynamic ones in React Router's ranking, so
      // this does not collide with `patients/:id` regardless of order.
      gate('patient.create', [{ path: 'patients/new', element: <PatientRegistrationPage /> }]),
      gate('patient.edit', [{ path: 'patients/:id/edit', element: <PatientEditPage /> }]),

      gate('report.view', [{ path: 'reports/:id', element: <ReportEntryPage /> }]),

      // The catalogue: each list is readable and writable on its own permission.
      ...catalogueRoutes({
        permission: 'labTest',
        path: 'lab-tests',
        List: LabTestListPage,
        Detail: LabTestDetailPage,
        Form: LabTestFormPage,
      }),
      ...catalogueRoutes({
        permission: 'testParameter',
        path: 'test-parameters',
        List: TestParameterListPage,
        Detail: TestParameterDetailPage,
        Form: TestParameterFormPage,
      }),
      ...catalogueRoutes({
        permission: 'procedure',
        path: 'test-procedures',
        List: TestProcedureListPage,
        Detail: TestProcedureDetailPage,
        Form: TestProcedureFormPage,
      }),
      ...catalogueRoutes({
        permission: 'reportComment',
        path: 'report-comments',
        List: ReportCommentListPage,
        Detail: ReportCommentDetailPage,
        Form: ReportCommentFormPage,
      }),
      ...catalogueRoutes({
        permission: 'reportRemark',
        path: 'report-remarks',
        List: ReportRemarkListPage,
        Detail: ReportRemarkDetailPage,
        Form: ReportRemarkFormPage,
      }),
      ...catalogueRoutes({
        permission: 'antibiotic',
        path: 'antibiotics',
        List: AntibioticListPage,
        Detail: AntibioticDetailPage,
        Form: AntibioticFormPage,
      }),
      ...catalogueRoutes({
        permission: 'organism',
        path: 'organisms',
        List: OrganismListPage,
        Detail: OrganismDetailPage,
        Form: OrganismFormPage,
      }),
      ...catalogueRoutes({
        permission: 'department',
        path: 'departments',
        List: DepartmentListPage,
        Detail: DepartmentDetailPage,
        Form: DepartmentFormPage,
      }),
      ...catalogueRoutes({
        permission: 'consultant',
        path: 'consultants',
        List: ConsultantListPage,
        Detail: ConsultantDetailPage,
        Form: ConsultantFormPage,
      }),

      // The price list is a screen every role quotes from, so it has its own
      // permission — a receptionist may not edit a test but must be able to
      // tell a patient what one costs.
      gate('rateList.view', [{ path: 'rate-list', element: <RateListPage /> }]),
      gate('pricing.quote', [{ path: 'calculator', element: <CalculatorPage /> }]),

      // The shelf.
      gate('reagentItem.view', [
        { path: 'reagent-items', element: <InventoryPage /> },
        { path: 'reagent-items/:id', element: <ReagentItemDetailPage /> },
      ]),
      gate('reagentItem.create', [{ path: 'reagent-items/new', element: <ReagentItemFormPage /> }]),
      gate('reagentItem.edit', [
        { path: 'reagent-items/:id/edit', element: <ReagentItemFormPage /> },
      ]),
      gate('recipe.view', [{ path: 'reagent-recipes', element: <RecipeListPage /> }]),
      gate('recipe.create', [{ path: 'reagent-recipes/new', element: <RecipeFormPage /> }]),
      gate('recipe.edit', [
        { path: 'reagent-recipes/:id', element: <RecipeFormPage /> },
        { path: 'reagent-recipes/:id/edit', element: <RecipeFormPage /> },
      ]),
      gate('supplier.view', [{ path: 'suppliers', element: <SupplierListPage /> }]),
      gate('supplier.create', [{ path: 'suppliers/new', element: <SupplierFormPage /> }]),
      gate('supplier.edit', [
        { path: 'suppliers/:id', element: <SupplierFormPage /> },
        { path: 'suppliers/:id/edit', element: <SupplierFormPage /> },
      ]),
      gate('stock.view', [{ path: 'stock-ledger', element: <StockLedgerPage /> }]),
      gate('lot.create', [{ path: 'stock-entry', element: <StockEntryPage /> }]),
      gate('whatsapp.audit', [{ path: 'whatsapp-log', element: <ShareAuditPage /> }]),

      // Reagent orders. Each transition has its own permission, enforced by
      // the server and mirrored by the detail page's buttons.
      gate('requisition.view', [
        { path: 'requisitions', element: <RequisitionListPage /> },
        { path: 'requisitions/:id', element: <RequisitionDetailPage /> },
      ]),
      gate('requisition.create', [{ path: 'requisitions/new', element: <RequisitionFormPage /> }]),
      gate('requisition.edit', [
        { path: 'requisitions/:id/edit', element: <RequisitionFormPage /> },
      ]),
      gate('requisition.receive', [
        { path: 'requisitions/:id/receive', element: <ReceiveStockPage /> },
      ]),

      // The clinical inbox.
      gate('orderRequest.view', [
        { path: 'order-requests', element: <OrderRequestListPage /> },
        { path: 'order-requests/:id', element: <OrderRequestDetailPage /> },
      ]),
      gate('orderRequest.create', [
        { path: 'order-requests/new', element: <OrderRequestFormPage /> },
      ]),
      gate('orderRequest.edit', [
        { path: 'order-requests/:id/edit', element: <OrderRequestFormPage /> },
      ]),
      gate('orderRequest.register', [
        { path: 'order-requests/:id/register', element: <RegisterRequestPage /> },
      ]),

      // Spreadsheet import. The list is safe to look at; the wizard *is* the
      // operation, so opening a run needs `import.run`.
      gate('import.view', [{ path: 'imports', element: <ImportListPage /> }]),
      gate('import.run', [
        // Static before dynamic, as with `patients/new`.
        { path: 'imports/new', element: <ImportWizardPage /> },
        { path: 'imports/:id', element: <ImportWizardPage /> },
      ]),

      // The lab's own identity, under /settings because there is one of it.
      gate('labProfile.edit', [{ path: 'settings/company', element: <CompanyDetailPage /> }]),
      gate('backup.export', [{ path: 'settings/backup', element: <BackupPage /> }]),
      gate('system.health', [{ path: 'settings/database', element: <DatabaseHealthPage /> }]),

      // Print templates are read by the print screen's picker for everyone, but
      // these screens design them: a template is the lab's stationery.
      gate('printTemplate.manage', [
        { path: 'print-templates', element: <TemplateListPage /> },
        { path: 'print-templates/new', element: <TemplateBuilderPage /> },
        // Both the row click and the Edit button land on the builder. A
        // template has no read-only view worth having — the thing you want to
        // look at is the PDF, which the builder shows.
        { path: 'print-templates/:id', element: <TemplateBuilderPage /> },
        { path: 'print-templates/:id/edit', element: <TemplateBuilderPage /> },
      ]),

      // The money screens — separate from `billing.view`: a receptionist sees
      // what *this patient* owes because they take the money; what the lab
      // pays its staff is a different question with a different answer.
      gate('finance.income', [{ path: 'income', element: <IncomePage /> }]),
      gate('finance.profitLoss', [{ path: 'profit-loss', element: <ProfitLossPage /> }]),
      gate('expense.view', [{ path: 'expenses', element: <ExpenseListPage /> }]),
      // Static before dynamic, as with `patients/new`.
      gate('expense.create', [{ path: 'expenses/new', element: <ExpenseFormPage /> }]),
      gate('expense.edit', [{ path: 'expenses/:id/edit', element: <ExpenseFormPage /> }]),

      /*
       * Operational analytics.
       *
       * One layout route with the sections as children, so the section nav can
       * live in the layout and each section keeps its own gate.
       */
      gate(
        ['analytics.view', 'analytics.staff', 'analytics.supply'],
        [
          {
            path: 'analytics',
            element: <AnalyticsLayout />,
            children: [
              gate('analytics.view', [
                { index: true, element: <OverviewPage /> },
                { path: 'volume', element: <VolumePage /> },
                { path: 'patients', element: <PatientMixPage /> },
                { path: 'flow', element: <FlowPage /> },
                { path: 'quality', element: <QualityPage /> },
              ]),
              gate('analytics.staff', [{ path: 'staff', element: <StaffPage /> }]),
              gate('analytics.supply', [{ path: 'supply', element: <SupplyPage /> }]),
            ],
          },
        ],
      ),

      // Year-wise history: the counts are a lab-operations fact; the money
      // columns come back null without `billing.view`, decided server-side.
      gate('finance.yearHistory', [{ path: 'year-history', element: <YearHistoryPage /> }]),

      gate('dayClose.view', [{ path: 'day-close', element: <DayClosePage /> }]),

      gate('panel.view', [
        { path: 'panels', element: <PanelListPage /> },
        { path: 'panels/:id', element: <PanelDetailPage /> },
      ]),
      gate('panel.create', [{ path: 'panels/new', element: <PanelFormPage /> }]),
      gate('panel.edit', [{ path: 'panels/:id/edit', element: <PanelFormPage /> }]),

      gate('staff.view', [
        { path: 'users', element: <UserListPage /> },
        { path: 'users/:id', element: <UserDetailPage /> },
      ]),
      gate('staff.create', [{ path: 'users/new', element: <UserFormPage /> }]),
      gate('staff.edit', [{ path: 'users/:id/edit', element: <UserFormPage /> }]),

      gate('role.view', [
        { path: 'roles', element: <RoleListPage /> },
        { path: 'roles/:id', element: <RoleFormPage /> },
      ]),
      gate('role.manage', [
        { path: 'roles/new', element: <RoleFormPage /> },
        { path: 'roles/:id/edit', element: <RoleFormPage /> },
      ]),

      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
